// =====================================================================
// Configuracoes da Agenda — painel admin white-label (Fase 3, S38).
// 8 abas que consomem os 22 endpoints v2 do n8n (workflow Admin v2).
// Multi-tenant: super-admin tem dropdown pra trocar de slug; admin local
// so ve o seu (companyId).
//
// E2: shell + Aba 1 (Identidade Visual) funcionais.
// E3-E4: demais abas com CRUDs.
// =====================================================================
import React, { useState, useEffect, useContext, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Box,
  Chip
} from "@material-ui/core";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ForbiddenPage from "../../components/ForbiddenPage";

import { AuthContext } from "../../context/Auth/AuthContext";

import {
  listAccessibleClinics,
  getClinicConfig
} from "../../services/clinicAdminService";

import IdentidadeVisual from "./tabs/IdentidadeVisual";
import Profissionais from "./tabs/Profissionais";
import Protocolos from "./tabs/Protocolos";
import Servicos from "./tabs/Servicos";
import Templates from "./tabs/Templates";
import Convenios from "./tabs/Convenios";
import Agenda from "./tabs/Agenda";
import Unidades from "./tabs/Unidades";
import AgendaPorData from "./tabs/AgendaPorData";
import FeatureFlags from "./tabs/FeatureFlags";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    backgroundColor: theme.palette.background.paper
  },
  mainPaper: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    flex: 1
  },
  headerExtra: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: "auto",
    flexWrap: "wrap"
  },
  tenantSelect: {
    flexGrow: 1,
    minWidth: 130
  },
  tabsRoot: {
    backgroundColor: theme.palette.type === "light" ? "#f7f7f7" : "#2a2a2a",
    borderRadius: 4,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  tab: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.875rem",
    minHeight: 44
  },
  bodyPaper: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    padding: theme.spacing(3),
    width: "100%"
  },
  placeholder: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  placeholderHint: {
    fontSize: "0.875rem",
    marginTop: theme.spacing(1),
    opacity: 0.7
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6)
  },
  emptyWrap: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: theme.palette.text.secondary
  },
  tenantChip: {
    fontWeight: 600,
    backgroundColor: `${theme.palette.primary.main}15`,
    color: theme.palette.primary.main,
    borderRadius: 6
  }
}));

// Catalogo das 8 abas. Render condicional embaixo.
const TABS = [
  { key: "identidade", label: "Identidade Visual" },
  { key: "profissionais", label: "Profissionais" },
  { key: "protocolos", label: "Protocolos" },
  { key: "convenios", label: "Convênios" },
  { key: "servicos", label: "Serviços" },
  { key: "agenda", label: "Agenda" },
  { key: "unidades", label: "Unidades", requiresDateAgenda: true },
  { key: "agenda_data", label: "Agenda por data", requiresDateAgenda: true },
  { key: "templates", label: "Templates Msg" },
  { key: "features", label: "Feature Flags" }
];

const ConfigAgenda = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [tab, setTab] = useState("identidade");
  const [clinics, setClinics] = useState([]);
  const [slug, setSlug] = useState(null);
  const [loadingClinics, setLoadingClinics] = useState(true);
  // feature flag por tenant: liga as abas Unidades/Agenda por data.
  // Fail-safe: erro ou ausência => false => abas escondidas (CLEE intacto).
  const [dateAgendaOn, setDateAgendaOn] = useState(false);

  // Permissao: admin OU super. Outros perfis veem ForbiddenPage.
  const hasAccess = user && (user.profile === "admin" || user.super);

  // Carrega tenants acessiveis no mount.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingClinics(true);
        const rows = await listAccessibleClinics();
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setClinics(list);
        // Default: primeiro slug. Admin local sempre tem so 1.
        if (list.length > 0 && !slug) {
          setSlug(list[0].slug);
        }
      } catch (err) {
        if (!cancelled) {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Erro carregando lista de clínicas";
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoadingClinics(false);
      }
    };
    if (hasAccess) {
      load();
    } else {
      setLoadingClinics(false);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]);

  // Lê a flag enable_date_agenda do tenant atual (fail-safe -> false).
  useEffect(() => {
    if (!slug) {
      setDateAgendaOn(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await getClinicConfig(slug);
        const cfg = data && data.config ? data.config : data;
        if (!cancelled) setDateAgendaOn(!!(cfg && cfg.enable_date_agenda));
      } catch (err) {
        if (!cancelled) setDateAgendaOn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleTenantChange = useCallback((e) => {
    setSlug(e.target.value);
  }, []);

  const handleTabChange = useCallback((_e, v) => {
    setTab(v);
  }, []);

  if (!hasAccess) {
    return <ForbiddenPage />;
  }

  if (loadingClinics) {
    return (
      <MainContainer className={classes.root}>
        <MainHeader>
          <Title>Configurações da Agenda</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.loadingWrap}>
            <CircularProgress />
          </div>
        </Paper>
      </MainContainer>
    );
  }

  if (clinics.length === 0) {
    return (
      <MainContainer className={classes.root}>
        <MainHeader>
          <Title>Configurações da Agenda</Title>
        </MainHeader>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.emptyWrap}>
            <Typography variant="body1">
              Nenhuma clínica acessível encontrada.
            </Typography>
            <Typography variant="caption" component="p" style={{ marginTop: 8 }}>
              Verifique se a sua empresa tem registro em <code>clinic_config</code>
              {" "}com <code>is_active = true</code>.
            </Typography>
          </div>
        </Paper>
      </MainContainer>
    );
  }

  // Encontra clinic atual pra exibir nome no chip.
  const currentClinic = clinics.find((c) => c.slug === slug) || clinics[0];

  // Abas visíveis: esconde as de agenda-por-data se a flag estiver off.
  const visibleTabs = TABS.filter(
    (t) => !t.requiresDateAgenda || dateAgendaOn
  );

  return (
    <MainContainer className={classes.root}>
      <MainHeader>
        <Title>Configurações da Agenda</Title>
        <Box className={classes.headerExtra}>
          {clinics.length > 1 ? (
            <FormControl
              size="small"
              variant="outlined"
              className={classes.tenantSelect}
            >
              <InputLabel id="tenant-label">Clínica</InputLabel>
              <Select
                labelId="tenant-label"
                value={slug || ""}
                onChange={handleTenantChange}
                label="Clínica"
              >
                {clinics.map((c) => (
                  <MenuItem key={c.slug} value={c.slug}>
                    {c.clinic_name || c.slug} ({c.slug})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Chip
              className={classes.tenantChip}
              label={`${currentClinic?.clinic_name || currentClinic?.slug} · ${currentClinic?.slug}`}
            />
          )}
        </Box>
      </MainHeader>

      <Paper className={classes.mainPaper} elevation={1}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          className={classes.tabsRoot}
        >
          {visibleTabs.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={t.label}
              className={classes.tab}
            />
          ))}
        </Tabs>

        <Paper className={classes.bodyPaper} elevation={0}>
          {tab === "identidade"    && slug && <IdentidadeVisual slug={slug} />}
          {tab === "profissionais" && slug && <Profissionais    slug={slug} />}
          {tab === "protocolos"    && slug && <Protocolos       slug={slug} />}
          {tab === "convenios"     && slug && <Convenios        slug={slug} />}
          {tab === "servicos"      && slug && <Servicos         slug={slug} />}
          {tab === "agenda"        && slug && <Agenda           slug={slug} />}
          {tab === "unidades"      && slug && dateAgendaOn && <Unidades      slug={slug} />}
          {tab === "agenda_data"   && slug && dateAgendaOn && <AgendaPorData slug={slug} />}
          {tab === "templates"     && slug && <Templates        slug={slug} />}
          {tab === "features"      && slug && <FeatureFlags     slug={slug} />}
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default ConfigAgenda;
